import { useEffect } from 'react';
import * as R from 'remeda';
import { yupResolver } from '@hookform/resolvers/yup';
import { useController, useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import * as yup from 'yup';
import { DetailDrawer } from '@rossum/ui';
import { IconSettings } from '@rossum/ui/icons/tabler';
import { Button, Stack, SvgIcon, Typography } from '@rossum/ui/material';
import TextFieldControl from '../../../../components/ReactHookForm/controls/TextFieldControl';
import {
  GITHUB_REGEX2_DOCS,
  WIKIPEDIA_MIME_TYPE_DOCS,
} from '../../../../constants/values';
import { linebreak, link } from '../../../../lib/formaterValues';
import { getUnsafe } from '../../../../lib/helpers';
import { clearValidationsOnChangeCreator } from '../../../../lib/messages';
import { clearValidationMessagesAction } from '../../../../redux/modules/validationMessages/action';
import { InputMessages } from '../../../../redux/modules/validationMessages/types';
import { DocumentRejectionConditions } from '../../../../types/inbox';
import {
  formatFileNameList,
  formatMimeTypeList,
  joinElementsByNewline,
  joinElementsWithComma,
  parseElements,
  parseElementsSeparatedByNewline,
} from './helpers';

type Props = {
  attachmentFilteringRules: DocumentRejectionConditions;
  clearValidationMessages: typeof clearValidationMessagesAction;
  closeDrawer: () => void;
  updateInbox: (
    _filterParams: Omit<DocumentRejectionConditions, 'enabled'>
  ) => void;
  validationMessages: InputMessages;
  open: boolean;
};

type Form = {
  resolutionHeight: string;
  resolutionWidth: string;
  fileSizeLessThanKB: string;
  mimeTypes: string;
  fileNameRegexes: string;
};

const numberOrNullRegex = /(^[+-]?([0-9]*[.])?[0-9]+$|^$)/;
const mimeTypesRegex = /^[\w*?]+\/[\w\-_.+*?]+$/;

const filteringRulesValidationSchema = (intl: IntlShape) =>
  yup.object().shape({
    resolutionHeight: yup
      .string()
      .matches(
        numberOrNullRegex,
        intl.formatMessage({
          id: 'containers.settings.queues.email.filteringRules.resolutionHeight.mustBeNumber',
        })
      )
      .required(
        intl.formatMessage({
          id: 'containers.settings.queues.email.filteringRules.resolutionHeight.required',
        })
      ),
    resolutionWidth: yup
      .string()
      .matches(
        numberOrNullRegex,
        intl.formatMessage({
          id: 'containers.settings.queues.email.filteringRules.resolutionWidth.mustBeNumber',
        })
      )
      .required(
        intl.formatMessage({
          id: 'containers.settings.queues.email.filteringRules.resolutionWidth.required',
        })
      ),
    fileSizeLessThanKB: yup
      .string()
      .matches(
        numberOrNullRegex,
        intl.formatMessage({
          id: 'containers.settings.queues.email.filteringRules.fileSizeLessThanKB.mustBeNumber',
        })
      )
      .notRequired(),
    mimeTypes: yup
      .string()
      .test(
        'testMimeTypes',
        intl.formatMessage({
          id: 'containers.settings.queues.email.filteringRules.mimeTypes.invalidMimeTypes',
        }),
        value => {
          const mimeTypes = parseElements(value ?? '');
          return mimeTypes.every(type => type.match(mimeTypesRegex));
        }
      )
      .notRequired(),
    fileNameRegexes: yup.string().notRequired(),
  });

const FilteringRulesEditor = ({
  attachmentFilteringRules,
  clearValidationMessages,
  closeDrawer,
  updateInbox,
  validationMessages,
  open,
}: Props) => {
  const intl = useIntl();

  const {
    resolutionLowerThanPx,
    fileSizeLessThanB,
    mimeTypes,
    fileNameRegexes,
  } = attachmentFilteringRules;
  const [resWidth, resHeight] = resolutionLowerThanPx;

  const defaultValues = {
    resolutionHeight: resHeight?.toString(),
    resolutionWidth: resWidth?.toString(),
    fileSizeLessThanKB: fileSizeLessThanB
      ? (fileSizeLessThanB / 1000).toString()
      : '',
    mimeTypes: joinElementsWithComma(mimeTypes),
    fileNameRegexes: joinElementsByNewline(fileNameRegexes),
  };

  const {
    watch,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
    handleSubmit,
    control,
  } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(filteringRulesValidationSchema(intl)),
  });

  const watchFields = watch();

  const {
    field: { value: mimeTypesValue, onChange: mimeTypesOnChange },
  } = useController({
    name: 'mimeTypes',
    control,
  });

  const {
    field: { value: fileNameRegexesValue, onChange: fileNameRegexesOnChange },
    fieldState: { error: fileNameRegexesError },
  } = useController({
    name: 'fileNameRegexes',
    control,
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      mimeTypesOnChange(formatMimeTypeList(watchFields.mimeTypes));
      fileNameRegexesOnChange(formatFileNameList(watchFields.fileNameRegexes));
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, isSubmitSuccessful]);

  const canSubmit = !R.isDeepEqual(watchFields, defaultValues) && isValid;

  const onSubmit = ({
    resolutionHeight,
    resolutionWidth,
    fileSizeLessThanKB: formSize,
    mimeTypes: formMimeTypes,
    fileNameRegexes: formFileNames,
  }: Form) => {
    const resolution: [number, number] = [
      parseFloat(resolutionWidth),
      parseFloat(resolutionHeight),
    ];
    const size = parseFloat(formSize) * 1000;
    const mime = parseElements(formMimeTypes);
    const fileNames = parseElementsSeparatedByNewline(formFileNames);

    updateInbox({
      resolutionLowerThanPx: resolution,
      fileSizeLessThanB: size,
      mimeTypes: mime,
      fileNameRegexes: fileNames,
    });
  };

  const regexMessages = getUnsafe(validationMessages, [
    'filters',
    'documentRejectionConditions',
    'fileNameRegexes',
  ]);
  const regexMessage = regexMessages
    ? JSON.stringify(regexMessages)
    : undefined;

  const clearValidationsOnChange = clearValidationsOnChangeCreator(
    clearValidationMessages,
    'inbox',
    validationMessages
  );

  useEffect(() => {
    clearValidationsOnChange('filters')(R.doNothing());
  }, [clearValidationsOnChange, mimeTypesValue, fileNameRegexesValue]);

  const regexErrorMessage =
    typeof regexMessage === 'string'
      ? intl.formatMessage(
          {
            id: `containers.settings.queues.email.filteringRules.filters.apiError`,
          },
          { message: regexMessage }
        )
      : (fileNameRegexesError?.message ?? '');

  return (
    <DetailDrawer
      titleIcon={
        <SvgIcon fontSize="large">
          <IconSettings />
        </SvgIcon>
      }
      title={intl.formatMessage({
        id: 'containers.settings.queues.email.filteringRules.title',
      })}
      open={open}
      keepMounted={false}
      onClose={closeDrawer}
    >
      <Stack p={3}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({
                id: 'containers.settings.queues.email.filteringRules.description',
              })}
            </Typography>
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                {intl.formatMessage({
                  id: 'containers.settings.queues.email.filteringRules.resolution.title',
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({
                  id: 'containers.settings.queues.email.filteringRules.resolution.subtitle',
                })}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'flex-start' }}
              >
                <TextFieldControl
                  ControllerProps={{ name: 'resolutionWidth', control }}
                  sx={{ width: '12ch' }}
                  placeholder={intl.formatMessage({
                    id: 'containers.settings.queues.email.filteringRules.resolutionWidth.placeholder',
                  })}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pt: 2 }}
                >
                  ×
                </Typography>
                <TextFieldControl
                  ControllerProps={{ name: 'resolutionHeight', control }}
                  sx={{ width: '12ch' }}
                  placeholder={intl.formatMessage({
                    id: 'containers.settings.queues.email.filteringRules.resolutionHeight.placeholder',
                  })}
                />
              </Stack>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                {intl.formatMessage({
                  id: 'containers.settings.queues.email.filteringRules.fileSizeLessThanKB.title',
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({
                  id: 'containers.settings.queues.email.filteringRules.fileSizeLessThanKB.subtitle',
                })}
              </Typography>
              <TextFieldControl
                ControllerProps={{ name: 'fileSizeLessThanKB', control }}
                sx={{ width: '12ch' }}
                placeholder={intl.formatMessage({
                  id: 'containers.settings.queues.email.filteringRules.fileSizeLessThanKB.placeholder',
                })}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                {intl.formatMessage({
                  id: 'containers.settings.queues.email.filteringRules.mimeTypes.title',
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage(
                  {
                    id: 'containers.settings.queues.email.filteringRules.mimeTypes.subtitle',
                  },
                  { linebreak, link: link(WIKIPEDIA_MIME_TYPE_DOCS) }
                )}
              </Typography>
              <TextFieldControl
                ControllerProps={{ name: 'mimeTypes', control }}
                placeholder={intl.formatMessage({
                  id: 'containers.settings.queues.email.filteringRules.mimeTypes.placeholder',
                })}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                {intl.formatMessage({
                  id: 'containers.settings.queues.email.filteringRules.fileNameRegexes.title',
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage(
                  {
                    id: 'containers.settings.queues.email.filteringRules.fileNameRegexes.subtitle',
                  },
                  { linebreak, link: link(GITHUB_REGEX2_DOCS) }
                )}
              </Typography>
              <TextFieldControl
                ControllerProps={{ name: 'fileNameRegexes', control }}
                placeholder={intl.formatMessage({
                  id: 'containers.settings.queues.email.filteringRules.fileNameRegexes.placeholder',
                })}
                id="fileNameRegexes"
                multiline
                helperText={regexErrorMessage}
                error={!!regexErrorMessage}
              />
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: 'flex-end' }}
            >
              <Button
                variant="contained"
                color="primary"
                disabled={!canSubmit}
                type="submit"
              >
                {intl.formatMessage({ id: 'containers.settings.fields.save' })}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </DetailDrawer>
  );
};

export default FilteringRulesEditor;
